/* eslint-disable no-use-before-define */
import { useDebounce } from '@fuse/hooks';
import { CircularProgress, makeStyles, Paper, Typography } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import Autocomplete from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { MdControlPoint } from 'react-icons/md';

const useStyles = makeStyles((theme) => ({
  listBoxCreatable: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    color: '#6bcbe8',
    backgroundColor: '#e2ebf0',
    padding: '16px',
    cursor: 'pointer',
  },
  input: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#707070',
      },
    },
  },
}));

// FIXME: não está preparado para utilizar paginação...

function SelectCreatableInput({
  debug = false,
  id,
  label,
  mutiple = false,
  required = false,
  fullWidth = true,
  autoFocus = false,
  disabled = false,
  autoSelect = false,
  fetchData = async () => {},
  createData = async () => {},
  // Alternativa ao createData, para quando o cadastro exige mais que o texto digitado:
  // recebe o texto e assume o cadastro (ex.: abrir um modal próprio), sem o fluxo interno
  // de loading e mensagem de sucesso/erro.
  onCreateOption,
  onClearInput = () => {},
  onResetInput = () => {},
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  onKeyPress,
  onKeyUp,
  value,

  touched,
  error,

  getOptionLabel = (option) => option?.label,
  getOptionKey = (option) => option?.key,
  fetchOnFocus,
  tamanhoMinimo = 1,
  maxLength,
}) {
  const classes = useStyles();

  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatable, setCreatable] = useState(false);
  const [displayCreateSuccess, setDisplayCreateSuccess] = useState(false);
  const [displayCreateError, setDisplayCreateError] = useState(false);
  const [scrollCurrentPosition, setScrollCurrentPosition] = useState(0); // Estado para controlar a posição do scroll
  const [page, setPage] = useState(0); // Estado para controlar a página atual
  const [hasMore, setHasMore] = useState(true); // Estado para controlar se há mais dados para carregar

  useEffect(() => {
    const key = getOptionKey(value);
    if (key !== undefined && key !== null && key !== '') {
      setInputValue(getOptionLabel(value) || '');
    }
    // Memoização intencional: as funções/props são recriadas a cada render e incluí-las
    // anularia o memo; o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (loading) {
      setCreatable(false);
      return;
    }

    const inputTrimmed = inputValue.trim();
    const isValidoTamanhoMinimo = inputTrimmed.length >= tamanhoMinimo;
    const hasMatchingOption =
      Array.isArray(options) &&
      options.some((option) => getOptionLabel(option).toLowerCase() === inputTrimmed.toLowerCase());

    if (onCreateOption && open && options.length === 0) {
      setCreatable(true);
      return;
    }

    if (isValidoTamanhoMinimo && inputTrimmed && !hasMatchingOption) {
      setCreatable(true);
      return;
    }

    setCreatable(false);
    // Memoização intencional: as funções/props são recriadas a cada render e incluí-las
    // anularia o memo; o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, options, inputValue, open, tamanhoMinimo, onCreateOption]);

  useEffect(() => {
    if (options.length > 0 && scrollCurrentPosition > 0 && hasMore) {
      setTimeout(() => {
        const listboxNode = document.querySelector('.MuiAutocomplete-listbox');
        if (listboxNode) {
          listboxNode.scrollTop = scrollCurrentPosition;
        }
      }, 5); // Atraso para garantir que o scroll seja aplicado após a renderização
    }
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const handleFilter = useDebounce(
    (value) => {
      setOptions([]);
      setLoading(true);
      setOpen(true);
      setPage(0); // Resetar a página ao iniciar uma nova busca
      setHasMore(true);
      (async () => {
        try {
          const data = await fetchData(value, 0); // Passar a página inicial
          setOptions(data);
          if (data.length === 0) {
            setHasMore(false);
          }
        } catch (error) {
          setOpen(false);
          console.error(error);
        } finally {
          setLoading(false);
        }
      })();
    },
    300,
    {}
  );

  const loadMore = async () => {
    if (!hasMore || loading) return;

    setLoading(true);
    try {
      const data = await fetchData(inputValue, page + 1); // Buscar a próxima página
      setOptions((prevOptions) => [...prevOptions, ...data]);
      setPage((prevPage) => prevPage + 1);
      if (data.length === 0) {
        setHasMore(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      multiple={mutiple}
      debug={debug}
      autoSelect={autoSelect}
      disabled={disabled}
      closeText="Fechar"
      openText="Abrir"
      noOptionsText={
        onCreateOption && !loading && options.length === 0 && !inputValue ? (
          'Nenhum registro cadastrado.'
        ) : inputValue.length >= tamanhoMinimo ? (
          'Nenhum registro encontrado.'
        ) : (
          <span
            className="MuiFormHelperText-root MuiFormHelperText-contained Mui-error Mui-required"
            style={{ display: 'flex', margin: '-15px 0px 0px 0px' }}
          >
            {`É necessário informar pelo menos ${tamanhoMinimo} caracteres no campo.`}
          </span>
        )
      }
      loadingText="Carregando..."
      clearText="Limpar"
      disableClearable={getOptionKey(value) === undefined}
      open={open}
      onOpen={(event) => {
        if (fetchOnFocus) {
          handleFilter(value || '');
        }
      }}
      onClose={(event) => {
        setOpen(false);
        setOptions([]);
      }}
      id={id}
      name={id}
      value={value}
      inputValue={inputValue}
      options={options}
      loading={loading}
      onInputChange={(event, value, reason) => {
        switch (reason) {
          case 'reset':
            setInputValue(value || '');
            onResetInput();
            break;
          case 'clear':
            setInputValue('');
            onClearInput();
            break;
          case 'input':
            if (value) {
              if (creatable) {
                setCreatable(false);
              }
              handleFilter(value);
            } else {
              setOpen(false);
            }
            setInputValue(value);
            break;
          default:
            break;
        }
      }}
      onChange={(event, value) => {
        if (value !== null) {
          onChange(value);
        }
      }}
      filterOptions={(options, state) => {
        return options;
      }}
      getOptionSelected={(option, value) => {
        return getOptionKey(option) === getOptionKey(value);
      }}
      getOptionLabel={(option) => {
        return getOptionLabel(option) || '';
      }}
      renderOption={(option) => {
        return <Typography noWrap>{getOptionLabel(option)}</Typography>;
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            className={classes.input}
            placeholder="Digite para pesquisar..."
            label={label}
            required={required}
            fullWidth={fullWidth}
            autoFocus={autoFocus}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{ ...params.inputProps, maxLength }}
            error={error && touched}
            helperText={
              displayCreateSuccess ? (
                <CreatableSuccess label={label} />
              ) : displayCreateError ? (
                <CreatableError label={label} />
              ) : touched && error ? (
                error
              ) : (
                undefined
              )
            }
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onKeyPress={onKeyPress}
            onKeyUp={onKeyUp}
          />
        );
      }}
      ListboxProps={{
        className: clsx('MuiAutocomplete-listbox', classes.listBoxOptions),
        onScroll: (event) => {
          const listboxNode = event.currentTarget;
          if (
            listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 && // Verificar se o scroll chegou ao final
            hasMore // Verificar se há mais dados para carregar
          ) {
            setScrollCurrentPosition(listboxNode.scrollTop);
            loadMore();
          }
        },
        style: { overflowAnchor: 'none' },
      }}
      PaperComponent={(props) => {
        return (
          <Paper elevation={1}>
            <Paper elevation={0} square {...props} />
            {creatable && (
              <div
                className={clsx(classes.listBoxCreatable, 'font-bold')}
                style={{ zIndex: '99999' }}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  if (onCreateOption) {
                    const trimmedInput = inputValue.trim();
                    setOpen(false);
                    setCreatable(false);
                    setInputValue('');
                    onCreateOption(trimmedInput);
                    return;
                  }

                  setLoading(true);
                  setOpen(true);
                  (async () => {
                    try {
                      await createData(inputValue);
                      setDisplayCreateSuccess(true);
                      setOpen(false);
                      setTimeout(() => {
                        setDisplayCreateSuccess(false);
                      }, 2000);
                    } catch (error) {
                      setDisplayCreateError(true);
                      setOpen(false);
                      setTimeout(() => {
                        setDisplayCreateError(false);
                      }, 1500);
                    } finally {
                      setLoading(false);
                    }
                  })();
                }}
              >
                <span className="text-lg font-bold">
                  <MdControlPoint />
                </span>
                <span className="ml-6">
                  <Typography className="font-bold" noWrap>
                    {inputValue.trim()
                      ? `Cadastrar "${inputValue.trim()}"`
                      : `Cadastrar ${label.toLowerCase()}`}
                  </Typography>
                </span>
              </div>
            )}
          </Paper>
        );
      }}
    />
  );
}

function CreatableSuccess({ label }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', color: '#43a047' }}>
      <CheckCircleOutlineIcon fontSize="inherit" style={{ marginRight: '2px' }} />
      {`${label} salvo com sucesso`}
    </span>
  );
}

function CreatableError({ label }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', color: 'red' }}>
      <HighlightOffIcon fontSize="inherit" style={{ marginRight: '2px' }} />
      {`Não foi possível criar o ${label.toLowerCase()}`}
    </span>
  );
}

export default SelectCreatableInput;
